import { SwapHoriz } from 'components/icons';
import { DateDefaultNowrap } from 'components/misc/DateDefaultNowrap';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { MainPayerVatNote } from 'pages/invoices/payers/MainPayerVatNote';
import { useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import { getBaseConversionDate, getInvoiceTotalsAmounts } from 'pages/invoices/util/invoiceUtil';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { FeatureFlag } from 'types/features';
import { InvoiceData, InvoiceTotals } from 'types/invoices';
import { cn } from 'utils/cn';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useInvoice } from '../../../hooks/useInvoice';
import InvoiceTotalsCorrections from './InvoiceTotalsCorrections';

interface Props {
    invoice: InvoiceData;
    invoiceTotals?: InvoiceTotals;
}

/** A total's label: 12px grey, right-aligned against its figure. */
export const TOTALS_LABEL_CELL = 'text-fg-secondary p-0 text-right text-[12px] leading-[1.375rem]';

/** The cell of a total's figure. */
export const TOTALS_VALUE_CELL = 'text-body1 text-fg min-w-[140px] p-0 pl-2 text-right tabular-nums';

/** The same total in the base currency, under the payment-currency one. */
export const TOTALS_BASE_VALUE = 'text-caption text-fg-secondary';

const InvoiceTotalsCard = ({ invoice, invoiceTotals }: Props) => {
    const prepaymentAmount = invoiceTotals?.paymentCurrency.invoice.prepaymentAmount ?? 0;
    const prepaymentAmountBaseCurrency = invoiceTotals?.baseCurrency.invoice.prepaymentAmount ?? 0;
    const paymentCurrencyTypeCode = invoiceTotals?.paymentCurrency.currencyTypeCode;
    const baseCurrencyTypeCode = invoiceTotals?.baseCurrency.currencyTypeCode;
    const { isPrepayment, isCorrection: isCorrectionInvoice } = useInvoice(invoice);
    // The main-payer VAT note sits under the amount payable block, which then ends the card without its curve.
    const { hasMultiplePayers: hasMainPayerVatNote } = useSharedPayment(invoice);
    const {
        totalWithVatPaymentCurrency,
        totalWithoutVatPaymentCurrency,
        totalWithVatBaseCurrency,
        totalWithoutVatBaseCurrency,
    } = getInvoiceTotalsAmounts(invoiceTotals);
    const vatRateAmountsPaymentCurrency = invoiceTotals?.paymentCurrency.vatRateAmounts ?? new Map();
    const vatRateAmountsBaseCurrency = invoiceTotals?.baseCurrency.vatRateAmounts ?? new Map();
    const showBaseCurrency = baseCurrencyTypeCode !== paymentCurrencyTypeCode;
    const isConvertBaseFromPaymentCurrency = useIsFeatureEnabled(
        FeatureFlag.INVOICING_CONVERT_BASE_CURRENCY_FROM_PAYMENT_CURRENCY
    );
    const paymentToBaseCurrencyConversionDate = getBaseConversionDate(invoice, isConvertBaseFromPaymentCurrency);

    return (
        <div>
            <div className="flex items-start justify-between p-6">
                {showBaseCurrency && invoice.baseCurrencyPaymentCurrencyConversionRate ? (
                    <div className="flex flex-col gap-1">
                        <div className="bg-paper inline-flex items-center rounded-[4px] px-3 py-2">
                            <SwapHoriz className="text-fg-secondary -mr-1.5 text-[18px]" />
                            <div className="flex items-center gap-0.5 pl-2 leading-[1.375rem]">
                                <p className="text-[13px] font-semibold">1 {paymentCurrencyTypeCode}</p>
                                <p className="text-fg-secondary text-[12px]">=</p>
                                <p className="text-[13px] font-semibold">
                                    {Number(Number(invoice.baseCurrencyPaymentCurrencyConversionRate).toFixed(8))}{' '}
                                    {baseCurrencyTypeCode}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col pl-3">
                            <p className="text-fg-secondary new:text-caption text-[11px] leading-[1.375rem] italic">
                                <FormattedMessage id="invoices.totals-tab.exchange-rate" />
                            </p>
                            {paymentToBaseCurrencyConversionDate && (
                                <DateDefaultNowrap
                                    dateValue={paymentToBaseCurrencyConversionDate}
                                    className="text-fg-secondary new:text-caption text-[11px]"
                                />
                            )}
                        </div>
                    </div>
                ) : (
                    <div />
                )}
                <table>
                    <tbody>
                        {isCorrectionInvoice ? (
                            <InvoiceTotalsCorrections invoice={invoice} invoiceTotals={invoiceTotals} />
                        ) : null}
                        <tr key="amount-wo-vat">
                            <td className={TOTALS_LABEL_CELL}>
                                <FormattedMessage id="invoices.totals-tab.total-amount-wo-vat" />
                            </td>
                            <td className={TOTALS_VALUE_CELL}>
                                <MoneyDisplay
                                    amount={totalWithoutVatPaymentCurrency}
                                    currency={paymentCurrencyTypeCode}
                                />
                            </td>
                        </tr>
                        {showBaseCurrency ? (
                            <tr key="amount-wo-vat-base-currency">
                                <td />
                                <td className={TOTALS_VALUE_CELL}>
                                    <span className={TOTALS_BASE_VALUE}>
                                        <MoneyDisplay
                                            amount={totalWithoutVatBaseCurrency}
                                            currency={baseCurrencyTypeCode}
                                        />
                                    </span>
                                </td>
                            </tr>
                        ) : null}
                        {Object.entries(vatRateAmountsPaymentCurrency).map(([vatRate, amount]) => (
                            <React.Fragment key={`vat-${vatRate}`}>
                                <tr key={`vat-${vatRate}-payment-currency`}>
                                    <td className={TOTALS_LABEL_CELL}>
                                        <FormattedMessage
                                            id="invoices.totals-tab.vat-rate-with-colon"
                                            values={{ rate: vatRate }}
                                        />
                                    </td>
                                    <td className={TOTALS_VALUE_CELL}>
                                        <MoneyDisplay amount={amount} currency={paymentCurrencyTypeCode} />
                                    </td>
                                </tr>
                                {showBaseCurrency ? (
                                    <tr key={`vat-${vatRate}-base-currency`}>
                                        <td />
                                        <td className={TOTALS_VALUE_CELL}>
                                            <span className={TOTALS_BASE_VALUE}>
                                                <MoneyDisplay
                                                    amount={
                                                        (
                                                            vatRateAmountsBaseCurrency as unknown as Record<
                                                                string,
                                                                number
                                                            >
                                                        )[vatRate] ?? 0
                                                    }
                                                    currency={baseCurrencyTypeCode}
                                                />
                                            </span>
                                        </td>
                                    </tr>
                                ) : null}
                            </React.Fragment>
                        ))}
                        <tr key="amount-with-vat">
                            <td className={TOTALS_LABEL_CELL}>
                                <FormattedMessage id="invoices.totals-tab.total-amount-with-vat" />
                            </td>
                            <td className={TOTALS_VALUE_CELL}>
                                <MoneyDisplay amount={totalWithVatPaymentCurrency} currency={paymentCurrencyTypeCode} />
                            </td>
                        </tr>
                        {showBaseCurrency ? (
                            <tr key="amount-with-vat-base-currency">
                                <td />
                                <td className={TOTALS_VALUE_CELL}>
                                    <span className={TOTALS_BASE_VALUE}>
                                        <MoneyDisplay
                                            amount={totalWithVatBaseCurrency}
                                            currency={baseCurrencyTypeCode}
                                        />
                                    </span>
                                </td>
                            </tr>
                        ) : null}
                        {!isPrepayment && (
                            <tr key="prepayments">
                                <td className={TOTALS_LABEL_CELL}>
                                    <FormattedMessage id="invoices.totals-tab.prepayments" />
                                </td>
                                <td className={TOTALS_VALUE_CELL}>
                                    <MoneyDisplay amount={prepaymentAmount * -1} currency={paymentCurrencyTypeCode} />
                                </td>
                            </tr>
                        )}
                        {!isPrepayment && showBaseCurrency ? (
                            <tr key="prepayments-base-currency">
                                <td />
                                <td className={TOTALS_VALUE_CELL}>
                                    <span className={TOTALS_BASE_VALUE}>
                                        <MoneyDisplay
                                            amount={prepaymentAmountBaseCurrency * -1}
                                            currency={baseCurrencyTypeCode}
                                        />
                                    </span>
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </div>
            {/* The amount payable, on the secondary colour lightened by 30% (70% of it over the white card). */}
            <div
                className={cn(
                    'bg-secondary/70 text-secondary-foreground flex justify-end px-6 py-[18px]',
                    !hasMainPayerVatNote && 'rounded-b-[7px]'
                )}
            >
                <table>
                    <tbody>
                        <tr key="amount-payable">
                            <td className="p-0 text-[12px] leading-[1.375rem] font-semibold">
                                <FormattedMessage id="invoices.totals-tab.amount-payable" />
                            </td>
                            <td className={cn(TOTALS_VALUE_CELL, 'text-secondary-foreground font-semibold')}>
                                <MoneyDisplay
                                    amount={totalWithVatPaymentCurrency - prepaymentAmount}
                                    currency={paymentCurrencyTypeCode}
                                />
                            </td>
                        </tr>
                        {showBaseCurrency ? (
                            <tr key="amount-payablet-base-currency">
                                <td />
                                <td className={TOTALS_VALUE_CELL}>
                                    <span className={cn(TOTALS_BASE_VALUE, 'new:text-secondary-foreground')}>
                                        <MoneyDisplay
                                            amount={totalWithVatBaseCurrency - prepaymentAmountBaseCurrency}
                                            currency={baseCurrencyTypeCode}
                                        />
                                    </span>
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </div>
            <MainPayerVatNote invoice={invoice} className="px-6 py-3" />
        </div>
    );
};

export default InvoiceTotalsCard;
